import { useCallback, useEffect, useState } from 'react';

import { COOKIE_KEYS as CORE_COOKIE_KEYS } from '@core-ui/common';
import { CORE_PAGES } from '@core-ui/components';
import { COOKIE_KEYS } from '@moniebook-web/types';
import { getItem, logout, removeAll, setItem } from '@moniebook-web/utils';
import { useQueryClient } from '@tanstack/react-query';
import { type EventsType, useIdleTimer } from 'react-idle-timer';
import { useNavigate } from 'react-router';

import { GHOST_SESSION_PAGES } from '@/features/ghost-session/constants';
import { useSessionTokenPayload } from '@/features/ghost-session/context/session-token-payload-context';

import {
    INACTIVITY_DIALOG_DURATION_MS,
    MAXIMUM_INACTIVITY_DURATION_MS,
    ONE_SECOND_MS,
} from '../constants';
import { getCountDownText } from '../utils';

const events: EventsType[] = ['click', 'keypress', 'focus'];

export const useSessionTimer = () => {
    const token = getItem(COOKIE_KEYS.TOKEN_VARIABLE);
    const navigate = useNavigate();
    const [showInactiveModal, setShowInactiveModal] = useState(false);
    const [countDown, setCountDown] = useState(INACTIVITY_DIALOG_DURATION_MS);
    const queryClient = useQueryClient();
    const { isGhostSession } = useSessionTokenPayload();

    const handleOnPrompt = () => {
        setShowInactiveModal(true);
    };

    const handleAutoLogout = useCallback(() => {
        queryClient.clear();

        if (isGhostSession) {
            setTimeout(() => {
                removeAll();
                navigate(GHOST_SESSION_PAGES.EXPIRED);
            }, 50);
            return;
        }

        setItem(CORE_COOKIE_KEYS.AUTO_LOGGED_OUT, 'true');
        setTimeout(() => {
            removeAll();
            navigate(CORE_PAGES.AUTH.AUTO_LOGGED_OUT);
        }, 50);
    }, [navigate, queryClient, isGhostSession]);

    const { reset, pause, getRemainingTime } = useIdleTimer({
        disabled: !token,
        timeout: MAXIMUM_INACTIVITY_DURATION_MS,
        promptBeforeIdle: INACTIVITY_DIALOG_DURATION_MS,
        onIdle: handleAutoLogout,
        onPrompt: handleOnPrompt,
        events,
        crossTab: true,
    });

    const handleContinueSession = useCallback(() => {
        reset();
        setShowInactiveModal(false);
    }, [reset]);

    const handleLogout = useCallback(() => {
        pause();
        queryClient.clear();
        logout(navigate, queryClient)();
    }, [navigate, pause, queryClient]);

    useEffect(() => {
        if (!showInactiveModal) return;

        const interval = setInterval(() => {
            setCountDown(getRemainingTime());
        }, ONE_SECOND_MS);

        return () => clearInterval(interval);
    }, [showInactiveModal, getRemainingTime]);

    return {
        showInactiveModal,
        countDown: getCountDownText(countDown),
        handleLogout,
        handleContinueSession,
        handleAutoLogout,
    };
};
